/*
 * This file contains a move-in date picker component that allows users to select a move-in date and displays lease terms.
 */

import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./movinDateModal.scss";
import closeIcon from "../../../assets/images/Close.svg";
import { format } from "date-fns";
import fetchData from "../../../utils/fetchData";
import { formatCurrency } from "../../../utils/util";
import clsx from "clsx";
import { traceEvent, traceFirstMeaningfulAction } from "../../../utils/tracing";

Modal.setAppElement("#root");

const MoveInDatePicker = ({
  isOpen,
  onClose,
  floorplanId,
  unitId,
  moveInStartDate,
  allowedDate,
}) => {
  // Helper function to safely format dates
  const safeFormatDate = (date) => {
    if (!date) return format(new Date(), "MM/dd/yyyy");
    const dateObj = new Date(date);
    return isNaN(dateObj.getTime()) ? format(new Date(), "MM/dd/yyyy") : format(dateObj, "MM/dd/yyyy");
  };

  const [selectedDate, setSelectedDate] = useState(() => {
    const minAllowedDate = new Date(allowedDate);
    const currentDate = new Date();
    const initialDate = moveInStartDate ? new Date(moveInStartDate) : (currentDate > minAllowedDate ? currentDate : minAllowedDate);
    return safeFormatDate(initialDate);
  });

  useEffect(() => {
    if (isOpen) {
      const parsedMoveInDate = moveInStartDate ? new Date(moveInStartDate) : null;
      const parsedAllowedDate = new Date(allowedDate);
      
      if (parsedMoveInDate && parsedAllowedDate && parsedMoveInDate > parsedAllowedDate) {
        setSelectedDate(safeFormatDate(parsedMoveInDate));
      } else {
        const minAllowedDate = new Date(allowedDate);
        const currentDate = new Date();
        setSelectedDate(safeFormatDate(currentDate > minAllowedDate ? currentDate : minAllowedDate));
      }
    }
  }, [isOpen, moveInStartDate, allowedDate]);

  const [dateConfirmed, setDateConfirmed] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState("");
  const [leaseTerms, setLeaseTerms] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleDateChange = (date) => {
    setSelectedDate(safeFormatDate(date));
  };

  // This function handles the date change event.
  // It sets the selected date to the formatted date string.
  const selectMoveInDate = () => {
    setSelectedTerm("");
    setDateConfirmed(true);
    getLeaseTerms();
  };

  // Helper function to safely parse date
  const parseDate = (dateString) => {
    if (!dateString) return null;

    // If it's already a Date object, return it
    if (dateString instanceof Date) return dateString;

    // Try parsing the date
    const parsedDate = new Date(dateString);

    // Check if the date is valid
    if (isNaN(parsedDate.getTime())) {
      console.error("Invalid date value:", dateString);
      return null;
    }

    return parsedDate;
  };

  // Helper function to format a number to plain text
  const formatToPlainNumber = (value) => {
    return parseFloat(value.replace(/,/g, "").replace(/\.00$/, ""));
  };

  // This function fetches the lease terms for the selected floorplan and unit.
  // It sets the lease terms to an empty array and starts loading.
  // It fetches the data from the API and updates the lease terms and loading state accordingly.
  const getLeaseTerms = async () => {
    setLeaseTerms([]);
    setLoading(true);
    try {
      const response = await fetchData(
        `/wp-json/entrata/v1/getTermRent?floorplanid=${floorplanId}&unitid=${unitId}&moveInStartDate=${selectedDate}`
      );
      let property_id = response[0]?.apartment_id;
      let unit_space_id = response[0]?.unit_space_id;
      let floorplan_id = response[0]?.floorplan_id;
      if (response[0]?.other?.term_rent) {
        let leaseTerms = response[0]?.other?.term_rent.map((term) => {
          return {
            name: `${term["@attributes"]?.LeaseTerm} at ${formatCurrency(
              formatToPlainNumber(term["@attributes"]?.Rent)
            )}/mo`,
            id: term["@attributes"]?.LeaseTermId,
            url: `${
              process.env.REACT_APP_FP_LEASE_TERM_URL 
            }/property[id]/${property_id}/property_floorplan[id]/${floorplan_id}/unit_space[id]/${unit_space_id}/show_in_popup/false/from_check_availability/1/term_month/${
              term["@attributes"]?.LeaseTerm?.split(" Month")[0]
            }/selected_occupancy_type[id]/1/?lease_start_date=${selectedDate}`,
          };
        });
        setLeaseTerms(leaseTerms);
      }
    } catch (error) {
      console.error("Error fetching lease terms:", error);
    } finally {
      setLoading(false);
    }
  };

  // This function handles the price change event.
  // It sets the selected term to the value of the event target.
  const handlePriceChange = (event) => {
    traceEvent("LEASE_TERMS_SELECT_RADIO");
    setSelectedTerm(event.target.value);
  };

  // This function handles the modal close event.
  // It sets the selected date to the move-in start date if it exists, otherwise it sets it to null.
  // It sets the selected term to an empty string and the date confirmed state to false.
  // It calls the onClose function to close the modal.
  const onCloseModal = () => {
    if (moveInStartDate) {
      setSelectedDate(moveInStartDate);
    } else {
      setSelectedDate(null);
    }
    setSelectedTerm("");
    setDateConfirmed(false);
    onClose();
  };

  // This function renders the move-in date picker component.
  // It displays a calendar for selecting a move-in date if the date is not confirmed.
  // If the date is confirmed, it displays the lease terms and a back button to return to the date selection.
  // It also includes an "Apply Now" button that links to the selected lease term URL.
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onCloseModal}
      className="movein-date-modal"
    >
      <button className="close-modal" onClick={() => onCloseModal()}>
        <img src={closeIcon} alt="close" />
      </button>
      {!dateConfirmed ? (
        <>
          <h2 className="modal-title">Desired Move-In Date</h2>
          <Calendar
            onChange={handleDateChange}
            value={parseDate(selectedDate)}
            className="react-calendar"
            prev2Label={null}
            next2Label={null}
            minDetail="month"
            minDate={(() => {
              const minAllowedDate = new Date(allowedDate);
              const currentDate = new Date();
              return currentDate > minAllowedDate ? currentDate : minAllowedDate;
            })()}
          />
          <div className="button-container single-child">
            <button
              onClick={() => {
                traceEvent("MOVEIN_MODAL_NEXT_BTN");
                selectMoveInDate();
              }}
              disabled={!selectedDate}
            >
              Next
            </button>
          </div>
        </>
      ) : (
        <>
          <h2 className="modal-title">Select a Lease Terms</h2>
          <div className="lease-terms-container-wrapper">
            <form className="lease-terms-container">
              {loading ? (
                <p>Loading...</p>
              ) : leaseTerms?.length > 0 ? (
                leaseTerms?.map((term, index) => (
                  <label key={index}>
                    <input
                      type="radio"
                      name={`lease-term-${index}`}
                      value={term?.id}
                      checked={selectedTerm === term?.id}
                      onChange={handlePriceChange}
                    />
                    {term?.name}
                  </label>
                ))
              ) : (
                <p>No lease terms found</p>
              )}
            </form>
          </div>
          <div className="button-container">
            <button
              className="back-button"
              onClick={() => setDateConfirmed(false)}
            >
              Back
            </button>
            <a
              href={leaseTerms.find((obj) => obj.id === selectedTerm)?.url}
              target="_blank"
              rel="noreferrer"
              className={clsx(!selectedTerm ? "disabled" : "", "apply-now-btn")}
              onClick={() => { traceFirstMeaningfulAction(); traceEvent("MOVEIN_MODAL_APPLY_NOW"); }}
            >
              Apply Now
            </a>
          </div>
        </>
      )}
    </Modal>
  );
};

export default MoveInDatePicker;
